import * as Yup from 'yup';
import Grid from '@material-ui/core/Grid';
import { TextField } from 'mui-rff';
import CircularProgress from '@material-ui/core/CircularProgress';
import * as React from 'react';
import { useState } from 'react';
import { FormPage } from '../../../components/FormPage';
import { FormSection } from '../../../components/FormSection';
import { AppUserAccount } from '../../../types/generated';
import { IconButton, InputAdornment } from '@material-ui/core';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';

const UserCreateValidationScheme = Yup.object().shape({
    firstName: Yup.string().required('First Name is required'),
    lastName: Yup.string().required('Last Name is required'),
    email: Yup.string().required('Email is required'),
    password: Yup.string()
        .min(8, 'You must provide at least 8 characters for a password')
        .required('You must provide a password'),
    passwordConfirmation: Yup.string()
        .oneOf([Yup.ref('password'), null], 'Passwords must match')
        .required('You must confirm the password'),
});

const UserEditValidationScheme = Yup.object().shape({
    firstName: Yup.string().required('First Name is required'),
    lastName: Yup.string().required('Last Name is required'),
    password: Yup.string().min(8, 'You must provide at least 8 characters for a password'),
    passwordConfirmation: Yup.string()
        .required('Please confirm your password')
        .when('password', {
            is: password => password && password.length > 0,
            then: Yup.string().oneOf([Yup.ref('password')], "Password doesn't match"),
        }),
});

export const UserEditFormView: React.FC<{
    user?: Pick<AppUserAccount, 'firstName' | 'lastName' | 'email'> | null;
    profile?: any;
    loading: any;
    onSubmit: any;
    onCancel?: any;
}> = ({ user, profile, loading, onSubmit, onCancel }) => {
    const [showPassword, setShowPassword] = useState(false);

    if (user === undefined) {
        return <CircularProgress />;
    }

    const breadcrumbs = [{ label: 'Team', link: '/extranet/team' }];

    const validationSchema = user === null ? UserCreateValidationScheme : UserEditValidationScheme;

    return (
        <FormPage
            title={user ? 'Edit user' : 'Create user'}
            loading={loading}
            initialValues={
                user
                    ? user
                    : {
                          firstName: '',
                          lastName: '',
                          email: '',
                      }
            }
            validationSchema={validationSchema}
            onSubmit={onSubmit}
            onCancel={onCancel}
            breadcrumbs={!profile && breadcrumbs}
            render={() => (
                <>
                    <FormSection title="Details">
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    name="firstName"
                                    label="First Name"
                                    required
                                    fullWidth
                                    autoFocus
                                    margin="normal"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    name="lastName"
                                    label="Last Name"
                                    required
                                    fullWidth
                                    margin="normal"
                                />
                            </Grid>
                        </Grid>

                        <TextField
                            type="email"
                            name="email"
                            label="Email Address"
                            required
                            fullWidth
                            disabled={!!user}
                            margin="normal"
                        />
                    </FormSection>
                    <FormSection
                        title={user === null ? 'Set password' : 'Change password (optional)'}
                        description={
                            'If you are setting a password for another user, we recommend that the user changes it upon first login.'
                        }
                    >
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={12}>
                                <TextField
                                    name="password"
                                    label="Password"
                                    type={showPassword ? 'text' : 'password'}
                                    fullWidth
                                    margin="normal"
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowPassword(!showPassword)}
                                                >
                                                    {showPassword ? (
                                                        <VisibilityIcon />
                                                    ) : (
                                                        <VisibilityOffIcon />
                                                    )}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <TextField
                                    name="passwordConfirmation"
                                    label="Confirm password"
                                    type={showPassword ? 'text' : 'password'}
                                    fullWidth
                                    margin="normal"
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowPassword(!showPassword)}
                                                >
                                                    {showPassword ? (
                                                        <VisibilityIcon />
                                                    ) : (
                                                        <VisibilityOffIcon />
                                                    )}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </FormSection>
                </>
            )}
        />
    );
};
